import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Tabs, Tab } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { getColor } from 'helpers/utils';
import useToken from 'hooks/useToken';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  LineChart,
  LegendComponent
]);

const InvestmentBarChart = ({ height }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [data, setData] = useState([]);
    const [percentCurrent, setPercentCurrent] = useState([]);
    const [percentPrev, setPercentPrev] = useState([]);
    const { token, setToken } = useToken();
    const abortRef = useRef(null);

    const months = useMemo(() => ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'], []);
    const currentYear = new Date().getFullYear();

    const years = useMemo(() => {
        const yearList = [];
        for (let year = currentYear; year >= 2020; year--) {
            yearList.push(year);
        }
        return yearList;
    }, [currentYear]);

    useEffect(() => {
        if (!token) return;

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const fetchForYear = (year) => {
            const formData = new URLSearchParams();
            formData.append('anio', year.toString());

            return fetch('/api/marketing/ObtenerInversionRetorno', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': token ? 'Bearer ' + token : ''
                },
                body: formData.toString(),
                signal: controller.signal
            })
            .then((res) => {
                if (!res.ok) {
                    if (res.status === 401) {
                        setToken(null);
                        location.href = '';
                    }
                    throw new Error('Error fetching investment data');
                }
                return res.json();
            })
            .then((response) => {
                if (response.iResponseType === 1 && response.detalles) {
                    const dataset = JSON.parse(response.detalles);
                    const rows = dataset.table2 || [];
                    const rowsPercent = dataset.table3 || [];

                    const monthlyData = months.map((month) => ({
                        name: month,
                        investment: 0,
                        roi: 0
                    }));

                    const percentData = months.map(() => 0);

                    rows.forEach(item => {
                        const monthIndex = item.mes - 1;
                        if (monthIndex >= 0 && monthIndex < 12) {
                            monthlyData[monthIndex].investment = parseFloat(item.dInversionMXN) || 0;
                            monthlyData[monthIndex].roi = parseFloat(item.dRetornoMXN) || 0;
                        }
                    });

                    rowsPercent.forEach(item => {
                        const monthIndex = item.mes - 1;
                        if (monthIndex >= 0 && monthIndex < 12) {
                            percentData[monthIndex] = parseFloat(item.dROI) || 0;
                        }
                    });

                    return { monthlyData, percentData };
                }

                return { monthlyData: months.map((month) => ({ name: month, investment: 0, roi: 0 })), percentData: months.map(() => 0) };
            });
        };

        Promise.all([fetchForYear(selectedYear), fetchForYear(selectedYear - 1)])
            .then(([current, previous]) => {
                setData(current.monthlyData);
                setPercentCurrent(current.percentData);
                setPercentPrev(previous.percentData);
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                console.error('Error fetching investment data:', err);
                setData(months.map((month) => ({ name: month, investment: 0, roi: 0 })));
                setPercentCurrent(months.map(() => 0));
                setPercentPrev(months.map(() => 0));
            });

        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [selectedYear, token]);

    const chartOptions = useMemo(() => ({
        animation: false,
        tooltip: {
            trigger: 'axis',
            padding: [7, 10],
            backgroundColor: getColor('gray-100'),
            borderColor: getColor('gray-300'),
            textStyle: { color: getColor('dark') },
            borderWidth: 1,
            confine: true,
            axisPointer: {
                type: 'shadow'
            },
            formatter: function (params) {
                const formatCurrency = (value) => {
                    return new Intl.NumberFormat('es-MX', {
                        style: 'currency',
                        currency: 'MXN',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                    }).format(value);
                };

                let result = `${params[0].axisValue}<br/>`;
                params.forEach(param => {
                    const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
                    result += `${marker}${param.seriesName}: <b>${formatCurrency(param.value)}</b><br/>`;
                });
                return result;
            }
        },
        legend: {
            data: ['Inversión', 'Retorno'],
            textStyle: { color: getColor('gray-700') }
        },
        grid: { 
            top: '15%', 
            right: '5%', 
            bottom: '10%', 
            left: '10%' 
        },
        xAxis: {
            type: 'category',
            data: data.map(d => d.name),
            axisLine: {
                lineStyle: {
                    color: getColor('gray-300')
                }
            },
            axisLabel: {
                color: getColor('gray-600')
            }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: getColor('gray-600'),
                formatter: function (value) {
                    return new Intl.NumberFormat('es-MX', {
                        style: 'currency',
                        currency: 'MXN',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                        notation: 'compact',
                        compactDisplay: 'short'
                    }).format(value);
                }
            },
            splitLine: {
                lineStyle: {
                    color: getColor('gray-200'),
                    type: 'dashed'
                }
            }
        },
        series: [
            {
                name: 'Inversión',
                type: 'bar',
                data: data.map(d => d.investment),
                itemStyle: {
                    color: '#8884d8'
                }
            },
            {
                name: 'Retorno',
                type: 'bar',
                data: data.map(d => d.roi),
                itemStyle: {
                    color: '#82ca9d'
                }
            }
        ]
    }), [data]);

    const percentOptions = useMemo(() => {
        const formatPercent = (v) => `${v.toFixed(1)}%`;

        const currentPercents = percentCurrent.length ? percentCurrent : months.map(() => 0);
        const prevPercents = percentPrev.length ? percentPrev : months.map(() => 0);

        return {
            animation: false,
            tooltip: {
                trigger: 'axis',
                padding: [7, 10],
                backgroundColor: getColor('gray-100'),
                borderColor: getColor('gray-300'),
                textStyle: { color: getColor('dark') },
                borderWidth: 1,
                confine: true,
                axisPointer: { type: 'line' },
                formatter: function (params) {
                    let result = `${params[0].axisValue}<br/>`;
                    params.forEach(param => {
                        const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
                        result += `${marker}${param.seriesName}: <b>${formatPercent(param.value)}</b><br/>`;
                    });
                    return result;
                }
            },
            legend: {
                data: [String(selectedYear), String(selectedYear - 1)],
                textStyle: { color: getColor('gray-700') }
            },
            grid: { top: '15%', right: '5%', bottom: '10%', left: '10%' },
            xAxis: {
                type: 'category',
                data: months,
                axisLine: { lineStyle: { color: getColor('gray-300') } },
                axisLabel: { color: getColor('gray-600') }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: getColor('gray-600'),
                    formatter: function (value) { return `${value}%`; }
                },
                splitLine: { lineStyle: { color: getColor('gray-200'), type: 'dashed' } }
            },
            series: [
                {
                    name: String(selectedYear),
                    type: 'line',
                    smooth: true,
                    data: currentPercents,
                    itemStyle: { color: '#8884d8' }
                },
                {
                    name: String(selectedYear - 1),
                    type: 'line',
                    smooth: true,
                    data: prevPercents,
                    itemStyle: { color: '#82ca9d' }
                }
            ]
        };
    }, [percentCurrent, percentPrev, selectedYear, months]);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Form.Select 
                    size="sm" 
                    value={selectedYear} 
                    style={{ width: 120, marginRight: 12 }} 
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </Form.Select>
            </div>

            <Tabs defaultActiveKey="porcentajes" id="roi-tabs" className="mb-3">
                <Tab eventKey="montos" title="Montos">
                    <ReactEChartsCore 
                        echarts={echarts} 
                        option={chartOptions} 
                        style={{ height: `${height}px` }}
                        opts={{ renderer: 'canvas' }}
                    />
                </Tab>
                <Tab eventKey="porcentajes" title="Porcentajes ROI">
                    <ReactEChartsCore 
                        echarts={echarts} 
                        option={percentOptions} 
                        style={{ height: `${height}px` }}
                        opts={{ renderer: 'canvas' }}
                    />
                </Tab>
            </Tabs>
        </div>
    );
};

InvestmentBarChart.propTypes = {
    height: PropTypes.number
};

InvestmentBarChart.defaultProps = {
    height: 300
};

export default InvestmentBarChart;
